import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setProgress, setToast } from "../../../redux/slice/system";
import { ProjectAPI } from "../../../axios/api";
import {
  useAbortedEffect,
  useSearch,
  useTable,
  usePop,
  useConfirm,
  useFilterList,
} from "../../../components/hooks";

import ModuleBreadCrumb from "../../../components/breadCrumb";
import ModulePopProjectPreview from "./popPreview";
import {
  formatOptions,
  setPageSetting,
  formatNumber,
  copyToClipboard,
} from "../../../service/common";
import { system_dropdown, system_table } from "../../../service/system";
import { rootUrl } from "../../../service/runtimeUrl";
import { option_enabled } from "../../../service/option/common";

import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";

export default function BackProjectList() {
  const route_navigate = useNavigate();
  const dispatch = useDispatch();
  const crumbItem = [{ key: 1, label: "專案管理" }];
  const [reload, setReload] = useState({ list: false });
  const {
    setTbData,
    setTbFirstIndex,
    setTbRows,
    setTbColumns,
    renderTable,
    setPageAction,
  } = useTable();
  // 篩選條件
  const defaultFilter = {
      page: 1,
      size: system_table.size,
      status: null,
    },
    pagePath = "/project";
  const {
    filterData,
    setFilterData,
    search,
    setSearch,
    changeFilter,
    renderSearchWrap,
  } = useSearch({
    defaultFilter,
    pagePath: [pagePath],
    tbSetting: { setTbFirstIndex, setTbRows },
    reloadAction: () => setReload((state) => ({ ...state, list: true })),
  });
  const { confirmOption, setConfirmOption, renderConfirm } = useConfirm();
  const { getFilterListData } = useFilterList();
  const [apiData, setApiData] = useState({
    term: "",
    data: [],
  });
  const { isPop, popOption, openPop, closePop, renderDialog } = usePop([
    {
      key: "preview",
      title: () => "預覽",
      isOpen: false,
    },
  ]);
  const redux_user = useSelector((state) => state.user);

  useEffect(() => {
    const action = ({ startIndex, rows }) => {
      let temp = {
        ...search,
        page: Math.floor(startIndex / rows) + 1,
        size: rows,
      };
      setFilterData(temp);
      setSearch(temp);
    };
    setPageAction(
      () =>
        ({ startIndex, rows }) =>
          action({ startIndex, rows }),
    );
  }, [search]);

  // 取得列表資料
  useAbortedEffect(
    (signal) => {
      if (!reload.list) return;

      dispatch(setProgress({ show: true }));

      setPageSetting({ params: search, page: pagePath });

      let params = {
        ...search,
      };

      ProjectAPI.getProjectList({
        data: params,
        options: { signal },
      }).then((result) => {
        let { Response, data, message } = result;

        if (Response === 1) {
          setApiData((state) => ({ ...state, data }));

          setReload((state) => ({
            ...state,
            list: false,
          }));
        } else {
          dispatch(
            setToast({
              severity: "error",
              summary: message,
              detail: "",
            }),
          );
        }

        dispatch(setProgress({ show: false }));
      });
    },
    [reload],
  );

  useEffect(() => {
    setTbData(
      getFilterListData({
        data: apiData.data,
        filterKey: "name",
        serachTerm: apiData.term,
      }) || [],
    );
  }, [apiData]);

  const deleteAction = ({ data }) => {
    dispatch(setProgress({ show: true }));

    ProjectAPI.deleteProject({ path: `/${data.mappingURLID}` }).then(
      (result) => {
        let { Response } = result;

        if (Response === 1) {
          dispatch(
            setToast({
              severity: "success",
              summary: "成功刪除",
              detail: "",
            }),
          );
          setReload((state) => ({
            ...state,
            list: true,
          }));
        } else {
          dispatch(
            setToast({
              severity: "error",
              summary: "刪除失敗",
              detail: "",
            }),
          );
          dispatch(setProgress({ show: false }));
        }
      },
    );
  };

  useEffect(() => {
    const renderBtnAction = (data) => (
      <>
        <Button
          className="p-button-info p-button-sm mr-2"
          type="button"
          label="複製連結"
          onClick={() => copyText(data)}
        />
        <Button
          className="p-button-help p-button-sm mr-2"
          type="button"
          label="預覽"
          onClick={() =>
            openPop({
              type: "preview",
              data: { mappingURLID: data.mappingURLID },
            })
          }
        />
        <Button
          className="p-button-sm mr-2"
          type="button"
          label="編輯"
          onClick={() =>
            route_navigate(`/back${pagePath}/${data.mappingURLID}`)
          }
        />
        <Button
          id={`button_delete_${data.mappingURLID}`}
          className="p-button-danger p-button-sm"
          type="button"
          label="刪除"
          onClick={() =>
            setConfirmOption({
              setting: {
                target: document.getElementById(
                  `button_delete_${data.mappingURLID}`,
                ),
                message: "確認是否刪除?",
                icon: "pi pi-exclamation-triangle",
                visible: !confirmOption.setting.visible,
                acceptLabel: "是",
                rejectLabel: "否",
                accept: () => deleteAction({ data }),
                reject: () => null,
              },
              reOpen: !!confirmOption.setting.visible,
            })
          }
        />
      </>
    );

    setTbColumns([
      {
        field: "name",
        header: "專案名稱",
        className: "flex-grow-1",
        style: { flexBasis: "70px" },
      },
      {
        field: "projectID",
        header: "專案來源",
        className: "flex-grow-1",
        style: { flexBasis: "70px" },
      },
      {
        field: "id",
        header: "進階",
        className: "w-18rem froze-right",
        alignFrozen: "right",
        frozen: true,
        body: (data) => renderBtnAction(data),
      },
    ]);
  }, [pagePath, confirmOption]);

  const copyText = (data) => {
    try {
      let url = `${rootUrl}iframe/multi/${data.mappingURLID}?apiKey=`;
      copyToClipboard({
        text: url,
        errorAction: () =>
          dispatch(
            setToast({
              severity: "error",
              summary: "複製失敗",
              detail: "",
            }),
          ),
      });

      dispatch(
        setToast({
          severity: "success",
          summary: "已複製連結",
          detail: data.name,
        }),
      );
    } catch (e) {
      dispatch(
        setToast({
          severity: "error",
          summary: "複製失敗",
          detail: "",
        }),
      );
    }
  };

  return (
    <>
      {renderConfirm}

      <ModuleBreadCrumb crumbItem={crumbItem} />

      <h2 className="my-3 ml-1">專案管理</h2>

      <div className="border-round-lg bg-white p-3">
        {/* {renderSearchWrap({
          isShow: false,
          template: (
            <>
              <div className="col-12 md:col-3 flex align-items-start flex-column mt-2 md:mt-0">
                <label
                  htmlFor="status"
                  className="w-4rem white-space-nowrap text-left mb-1"
                >
                  啟用狀態
                </label>
                <Dropdown
                  name="status"
                  className="w-full"
                  value={filterData.status}
                  onChange={(e) => changeFilter(e)}
                  options={option_enabled}
                  emptyMessage={system_dropdown.empty}
                  showClear
                />
              </div>
              <div
                className={classNames(
                  "col-12 md:col-9 flex align-items-end mt-2 md:mt-0"
                )}
              >
                <Button
                  className="p-button-info w-auto ml-auto md:ml-0 px-3"
                  type="button"
                  icon="pi pi-search"
                  label="查詢"
                  onClick={() =>
                    setSearch({
                      ...filterData,
                      page: 1,
                    })
                  }
                />

                <Button
                  className="w-auto md:ml-auto ml-3 px-3"
                  type="button"
                  label="新增"
                  onClick={() => route_navigate(`/back${pagePath}/add`)}
                />
              </div>
            </>
          ),
        })} */}

        <div className="flex align-items-center">
          <InputText
            value={apiData.term}
            placeholder="專案查詢"
            onChange={(e) =>
              setApiData((state) => ({ ...state, term: e.target.value }))
            }
          />
          <Button
            type="button"
            icon="pi pi-refresh"
            className="p-button-secondary ml-2"
            onClick={() => setApiData((state) => ({ ...state, term: "" }))}
          />

          <Button
            className="w-auto ml-auto px-3"
            type="button"
            label="新增"
            onClick={() => route_navigate(`/back${pagePath}/add`)}
          />
        </div>

        {renderTable({
          className: "mt-2",
          isPaginator: false,
          options: { scrollable: true, scrollDirection: "both" },
        })}
      </div>

      {renderDialog({
        className: "w-11",
        children: (
          <>
            {isPop.preview?.isOpen && (
              <ModulePopProjectPreview
                prePopOption={popOption}
                closePrePop={closePop}
              />
            )}
          </>
        ),
      })}
    </>
  );
}
